import Checkbox from "./checkbox/index";
import { differenceInDays, differenceInHours, differenceInMinutes, format, isWithinInterval, parse, subDays } from "date-fns";
import { PolicyHelper } from "helpers";
import { formatMoney } from "helpers/policy";
import { PAID, PAST_DUE, DUE_NOW, DUE_SOON } from "./constants";
import { useScreenType } from "globals/screenTypeHook";

// Static image paths from public folder
const checkerV2 = "/assets/img/additionalbenefits_icons/policyChecker.svg";
const checker = "/assets/img/additionalbenefits_icons/cart_checker2.svg";
const disabledCartChecker = "/assets/img/additionalbenefits_icons/disabled-cart_checker.svg";
const emptySelection = "/assets/img/additionalbenefits_icons/empty_selection.svg";

/** Component to specify the status of the modal */
export function getStatus(flow, { effective_date, transaction_id }) {
	let status = "-";
	let style = {}; 

	if (transaction_id) {
		status = PAID;
		style = { color: "#12ff00" };
	} else {
		const now = new Date();
		let due = parse(effective_date, "MM/dd/yyyy", now);
		let diff = flow === "manage your policy" ? differenceInMinutes(now, due) : differenceInDays(now, due);
		
		if(flow === "manage your policy"){
			if (diff > 1) {
				status = PAST_DUE;
				style = "past-due";
			} 
		} else {
			// is more than 1 day late?
			if (diff > 5) {
				status = PAST_DUE;
				style = "past-due"; 
			}
			// is less than 3 days early
			else if (diff > -3) {
				status = DUE_NOW;
				style = "due-now";
			}
			// due within 3 - 7 days
			else if (diff >= -7) {
				status = DUE_SOON;
				style = "due-soon";
			}
			// otherwise ignore
		}
		
	}
	return { 
		status,
		style,
	};
}

/** Calculate the total based on `selected ` terms accounting for late fees */
export function calcExtensionTotal(terms, tax = 1) {
	return terms?.reduce((total, term) => {
		return (
			total +
			(term?.status !== PAID && term?.selected === true ? term?.payment_term_premium_with_tax + term?.late_fee : 0)
		);
	}, 0);
}

export function calcExtensionTotalAlt(terms, tax = 1) {
	return terms?.reduce((total, term) => {
		return total + (term?.status !== PAID ? term?.payment_term_premium_with_tax + term?.late_fee : 0);
	}, 0);
}

/**
 * calculates the proper payent plans normalized for AGIC
 *
 * @returns {Array} payment_terms
 * @param {Object} paymentPlan - the plan on policy
 * @param {Array} risks
 * @param {Number} taxPercent -
 */
export function recalculatePaymentPlans(flow, paymentPlan, risks, taxPercent, policy_start) {
	const is_older =
		new Date(process.env.REACT_APP_MODE === "development" ? "11/14/2021" : "12/21/2021") > new Date(policy_start);
	const fullRiderPremium = PolicyHelper.removeAllRiderPremiums(risks);
	/* if policy_start date is older then they will be charged in part else nothing*/
	const totalDeductions = is_older ? fullRiderPremium / paymentPlan?.payment_terms.length : 0;
	const totalDeductionsWithTax = totalDeductions * taxPercent;

	return paymentPlan?.payment_terms.map(term => {
		const { status, style } = getStatus(flow, term);
		const lateFee = status === PAST_DUE ? term.penalty_fee_amount * taxPercent : 0; 
		return {
			...term,
			status,
			style,
			selected: [PAID, PAST_DUE, DUE_NOW].includes(status),
			payment_term_premium_with_tax: term.payment_term_premium * taxPercent - totalDeductionsWithTax,
			payment_term_premium: term.payment_term_premium - totalDeductions,
			late_fee: lateFee,
		};
	});
}

export const checkCertPeriod = (validCert, policyStart, policyEnd, certEnd) => {
	if (validCert) {
		let start = parse(policyStart, "MM/dd/yyyy HH:mm:ss", new Date());
		let end = parse(policyEnd, "MM/dd/yyyy HH:mm:ss", new Date());
		certEnd = parse(certEnd, "MM/dd/yyyy HH:mm:ss", new Date());

		return isWithinInterval(certEnd, { start: start, end: end });
	}

	return false;
};

export const checkIfExtendable = ( missingDocuments) => {
	// const { risks } = policy;
	// let certExpiry = risks[0].veh_cert_expiry_date;
	// let createdCert = certExpiry ? true : false;
	// const validCertPeriod = checkCertPeriod(createdCert, policy.start_date, policy.end_date, certExpiry);  

	return missingDocuments?.[0]?.documents?.length === undefined;
};

/**
 * 1. Sends a payment agreement note if the user select any PART payment
 * 2. Stores the necessary data in redux to prefill certain fields on payments
 * 3. Navigates to payment plan breakdown page for PART payments
 */

export const toggleSelection = (index, status, localPlanTerms) => {
	if (status === PAST_DUE || status === PAID) {
		// not able to deselect
		return null;
	} else {
		let temp = [...localPlanTerms];
		temp[index].selected = !temp[index].selected;
		// recursively select all other plans beneath to true
		if (temp[index].selected === true) {
			for (let a = index - 1; a > 0; a--) {
				if (temp[a].status === PAID) break;
				temp[a].selected = true;
			}
		} else if (temp[index].selected === false) {
			// all policies above should be set to false
			for (let a = index + 1; a < temp.length; a++) {
				temp[a].selected = false;
			}
		}

		return temp;
	}
};
/**
 *
 * @param {Array} localPlanTerms
 * @param {Object} policy
 * @param {(a,b,c)=>void} onToggle
 */
export function planTermsMapping(localPlanTerms, policy, onToggle) {
 
	return localPlanTerms?.map((term, index) => {
		const paid = term?.transaction_id ? true : false;
		const lastTerm = index + 1 === policy?.paymentPlan?.payment_terms?.length;
		const { isMobile, isTablet, isDesktop, isLargeDesktop} = useScreenType();
		//let state = status(term)
		//const currrentDue = false; // TODO find attribut to identify these
		//const selected = paid || [DUE_NOW, PAST_DUE].includes(term.status);

		return (
			<>
			{
				isTablet || isDesktop || isLargeDesktop ? <div
					className={lastTerm ? "row-content" : index === 0 ? "row-content-first" : "row-content-alt"}
					key={`${index}pln`}
					style={{ background: "#F4F2F2" }}
				>
					<p className={`table-cell first ${paid ? "bill-paid" : ""}`}>{index + 1}</p>
					<p className={`table-cell ${paid ? "bill-paid" : ""}`}>{format(subDays(parse(term.effective_date, "MM/dd/yyyy", new Date()), 1), "d-MMM-yyyy")?.toUpperCase()}</p>

					<p className={`table-cell ${paid ? "bill-paid" : ""}`}>{ `JMD ${formatMoney(term.payment_term_premium_with_tax + (term.late_fee || 0))}`}</p>

					<p
						className={`table-cell position-relative last ${paid ? "bill-paid" : ""}`} 
						style={{
							color:
								term.status === PAST_DUE || term.status === DUE_NOW
									? "#ff0000"
									: term.status === DUE_SOON
									? "blue"
									: "#84bd00",
						}}
					>
						{term.status}
						<Checkbox
							checked={term.selected}
							onClick={term.status === PAST_DUE ? () => null : () => onToggle(index, term.status, localPlanTerms)}
							disabled={paid}
						/>
					</p> 
				</div> : <div style={{position: "relative"}}>
					<div style={{
						position: "absolute",
						top: 10,
						right: 10
					}}>
						<span style={{
							color: term.status === PAST_DUE || term.status === DUE_NOW
								? "#ff0000"
								: term.status === DUE_SOON
								? "blue"
								: "rgb(132 189 0)",
							background: term.status === PAST_DUE || term.status === DUE_NOW
								? "rgba(255, 0, 0, 0.25)"
								: term.status === DUE_SOON
								? "rgba(0, 0, 255, 0.25)"
								: "rgba(132, 189, 0, 0.25)",
							padding: "2.5px 10px",
							borderRadius: 35,
							fontSize: 12,
							minWidth: 50,
							display: "block",
							textAlign: "center"

						}}>
							{term.status}
						</span>
					</div>
					<label className={`product-card ${term.selected || term.status === "Paid" ? "selected" : ""} ${term.status === "Paid" && "paid" }`} style={{ borderRadius: 10 ,border: term.status === "Paid" ? "none" : "2px solid #00A8E0", color: term.status === "Paid" ? "#636B70" : "white", backgroundColor: term.status === "Paid" ?  "rgb(252 252 252)" : "white" }}>
						<div 
							className="card-container"
							style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}
						> 
							<div style={{display: "flex", margin: "auto"}}>
								<span className="payment-schedule-num">
									{(term.selected) ? (
										<img className="check-container" src={term.status === "Paid" ? disabledCartChecker : checkerV2} />
									):(
										<img className="check-container" src={emptySelection} />
									)}
									<input className="d-none" disabled={paid} onChange={term.status === PAST_DUE ? () => null : () => onToggle(index, term.status, localPlanTerms)} checked={term.selected} type="checkbox" name="verification-method" />
								</span>
							</div>
							 
							{
								<div className="terms">
									<div className="status" style={{color: term.status === "Paid" ? "#636B70" : "white"}}>
										<div >
											<strong style={{display: "none"}}>Status:</strong>{" "}
										</div>
										
									</div>
									<div className="am-due" style={{color: term.status === "Paid" ? "#636B70" : "black", display: "flex", justifyContent: "space-between"}}>
										{/* <div> <strong>{term.status === "Paid" ? "Amount Paid" : "Amount Due"}:</strong> </div> */}
										<div style={{display: "flex", fontWeight: "bold", fontSize: 13}}> <span style={{marginRight: "5px"}}>JMD</span> <span>{formatMoney(term.payment_term_premium_with_tax + (term.late_fee || 0))}</span>{" "} </div>
										
									</div>
									<div className="due-date" style={{color: term.status === "Paid" ? "#636B70" : "black"}}>
										<div>Due Date:{" "}</div>
										<div><span >{format(subDays(parse(term.effective_date, "MM/dd/yyyy", new Date()), 1), "d-MMM-yyyy")?.toUpperCase()}</span></div>
									</div>
								</div>
							}
							
						</div>
					</label>
				</div>
				}
			</> 
		);
	});
}
